import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

/* ─── AOS-like scroll animation hook ─────────────────────────────────────── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll("[data-aos]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("aos-animate");
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  });
}

/* ─── Constants ───────────────────────────────────────────────────────────── */
const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:3000")
  .replace(/\/$/, "")
  .replace(/\/api$/, "");

const LOGO_COLORS = ["#1a1a2e","#16213e","#0f3460","#533483","#2b2d42","#1b4332","#212529","#3a0ca3","#7b2d8b","#b5179e"];

function companyColor(name) {
  return LOGO_COLORS[((name || "?").charCodeAt(0)) % LOGO_COLORS.length];
}

/* ─── Auth helpers ────────────────────────────────────────────────────────── */
function readToken() {
  return (
    localStorage.getItem("th_employer_token") ||
    localStorage.getItem("th_token") ||
    localStorage.getItem("token") ||
    ""
  );
}
function readCandidateToken() {
  return (
    localStorage.getItem("th_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    ""
  );
}
function readCandidateUser() {
  try {
    const r =
      localStorage.getItem("th_user") ||
      localStorage.getItem("user") ||
      localStorage.getItem("authUser");
    return r ? JSON.parse(r) : null;
  } catch {
    return null;
  }
}

function authFetch(path, opts = {}) {
  const token = readToken();
  const apiPath = path.startsWith("/api/") ? path : `/api${path.startsWith("/") ? path : `/${path}`}`;
  return fetch(`${API_BASE}${apiPath}`, {
    credentials: "include",
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
}

function candidateFetch(path, opts = {}) {
  const token = readCandidateToken();
  return fetch(`${API_BASE}/api${path.startsWith("/") ? path : `/${path}`}`, {
    credentials: "include",
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
}

/* ─── Formatters ─────────────────────────────────────────────────────────── */
function formatCurrency(value) {
  if (value === null || value === undefined || value === "") return "Not shared";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(num);
}

function getInitials(name) {
  return (
    (name || "C")
      .split(" ")
      .map((part) => part.trim()[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "C"
  );
}

function toTextList(value) {
  if (Array.isArray(value)) return value.join(", ");
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  if (!text) return "";
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed.join(", ");
  } catch {}
  return text;
}

function toList(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === null || value === undefined) return [];
  const text = String(value).trim();
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
  } catch {}
  return text.split(",").map((item) => item.trim()).filter(Boolean);
}

function parseJson(value, fallback = {}) {
  if (!value) return fallback;
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function formatPostedDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  const diff = Math.floor((Date.now() - dt) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff <= 6) return `${diff} days ago`;
  if (diff <= 13) return "1 week ago";
  if (diff <= 20) return "2 weeks ago";
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function isNew(d) {
  return !!d && Math.floor((Date.now() - new Date(d)) / 86400000) <= 3;
}

function resolveLogoSrc(src) {
  if (!src) return "";
  if (src.startsWith("http") || src.startsWith("data:")) return src;
  return `${API_BASE}/${src.replace(/^\//, "")}`;
}

/* ─── Draft builders ─────────────────────────────────────────────────────── */
function buildSectionDraft(profile = {}) {
  const sections = parseJson(profile.public_sections, {});
  return {
    about: sections.about || profile.hiring_bio || "",
    contact: {
      recruiterName: sections.contact?.recruiterName || `${profile.first_name || ""} ${profile.last_name || ""}`.trim(),
      role: sections.contact?.role || profile.role || "",
      dept: sections.contact?.dept || profile.dept || "",
      email: sections.contact?.email || profile.email || "",
      phone: sections.contact?.phone || profile.phone || "",
      altPhone: sections.contact?.altPhone || profile.alt_phone || "",
      website: sections.contact?.website || profile.website || "",
      linkedin: sections.contact?.linkedin || profile.linkedin || "",
    },
    hiring: {
      summary: sections.hiring?.summary || "",
      companyType: sections.hiring?.companyType || profile.company_type || "",
      size: sections.hiring?.size || profile.size || profile.company_size || "",
      yearEstablished: sections.hiring?.yearEstablished || profile.year_established || "",
      branches: sections.hiring?.branches || profile.branches || "",
      openPositions: sections.hiring?.openPositions || profile.open_positions || "",
      urgency: sections.hiring?.urgency || profile.urgency || "",
      location: sections.hiring?.location || profile.city || "",
      address: sections.hiring?.address || profile.address || "",
      country: sections.hiring?.country || profile.country || "",
    },
    jobs: { headline: sections.jobs?.headline || "", featuredRoles: sections.jobs?.featuredRoles || "", notes: sections.jobs?.notes || "" },
    salaries: { headline: sections.salaries?.headline || "", notes: sections.salaries?.notes || "" },
    interviews: { headline: sections.interviews?.headline || "", notes: sections.interviews?.notes || "" },
    benefits: { headline: sections.benefits?.headline || "", items: sections.benefits?.items || "" },
    photos: { headline: sections.photos?.headline || "", items: sections.photos?.items || "" },
    posts: { headline: sections.posts?.headline || "", items: sections.posts?.items || "" },
  };
}

function buildDraft(profile = {}) {
  return {
    first_name: profile.first_name || "",
    last_name: profile.last_name || "",
    role: profile.role || "",
    dept: profile.dept || "",
    alt_phone: profile.alt_phone || "",
    company_email: profile.company_email || "",
    company: profile.company || "",
    industry: profile.industry || "",
    city: profile.city || "",
    state: profile.state || "",
    country: profile.country || "",
    address: profile.address || "",
    pin: profile.pin || "",
    size: profile.size || profile.company_size || "",
    website: profile.website || profile.company_website || "",
    linkedin: profile.linkedin || profile.linkedin_url || "",
    year_established: profile.year_established || "",
    branches: profile.branches || "",
    open_positions: profile.open_positions || "",
    urgency: profile.urgency || "",
    employment_types: toTextList(profile.employment_types),
    experience_levels: toTextList(profile.experience_levels),
    hiring_bio: profile.hiring_bio || profile.notes || profile.company_about || "",
    source: profile.source || "",
    company_logo: profile.company_logo || profile.logo_url || "",
    company_type: profile.company_type || "",
    gst: profile.gst || "",
  };
}

const FREE_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "proton.me",
  "protonmail.com",
  "icloud.com",
  "aol.com",
  "live.com",
  "mail.com",
]);

function cleanText(value) {
  return String(value ?? "").trim();
}

function normalizeUrl(value) {
  const text = cleanText(value);
  if (!text) return "";
  return /^https?:\/\//i.test(text) ? text : `https://${text}`;
}

function isValidHttpUrl(value) {
  try {
    const parsed = new URL(normalizeUrl(value));
    return /^https?:$/i.test(parsed.protocol) && !!parsed.hostname && parsed.hostname.includes(".");
  } catch {
    return false;
  }
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanText(value));
}

function hasCorporateDomain(value) {
  const email = cleanText(value).toLowerCase();
  const domain = email.split("@")[1] || "";
  return domain && !FREE_EMAIL_DOMAINS.has(domain);
}

function isValidPersonName(value) {
  return /^[A-Za-z][A-Za-z .'-]{1,49}$/.test(cleanText(value));
}

function isSuspiciousValue(value) {
  const text = cleanText(value).toLowerCase();
  if (!text) return false;
  if (/^(test|dummy|sample|asdf|qwerty|n\/a|na|null|none|abc)$/.test(text)) return true;
  if (/(.)\1{5,}/.test(text)) return true;
  return false;
}

function validateCompanyProfileDraft(inputDraft = {}) {
  const currentYear = new Date().getFullYear();
  const draft = { ...inputDraft };

  [
    "first_name","last_name","role","dept","company","industry","company_type","size",
    "company_email","city","state","country","address","pin","urgency","hiring_bio",
    "employment_types","experience_levels","source","alt_phone","website","linkedin","company_logo",
  ].forEach((key) => {
    if (key in draft) draft[key] = cleanText(draft[key]);
  });

  draft.website = normalizeUrl(draft.website);
  draft.linkedin = normalizeUrl(draft.linkedin);
  draft.company_logo = draft.company_logo ? normalizeUrl(draft.company_logo) : "";
  draft.gst = cleanText(draft.gst).toUpperCase();
  draft.year_established = cleanText(draft.year_established);
  draft.branches = cleanText(draft.branches);
  draft.open_positions = cleanText(draft.open_positions);

  const required = [
    ["first_name", "First Name"],
    ["last_name", "Last Name"],
    ["role", "Recruiter Role"],
    ["dept", "Department"],
    ["company", "Company Name"],
    ["industry", "Industry"],
    ["company_type", "Company Type"],
    ["size", "Company Size"],
    ["company_email", "Company Email"],
    ["website", "Website"],
    ["linkedin", "LinkedIn"],
    ["city", "City"],
    ["state", "State"],
    ["country", "Country"],
    ["address", "Address"],
    ["pin", "PIN / ZIP"],
    ["year_established", "Year Established"],
    ["open_positions", "Open Positions"],
    ["urgency", "Hiring Urgency"],
    ["hiring_bio", "About / Hiring Bio"],
    ["employment_types", "Employment Types"],
    ["experience_levels", "Experience Levels"],
  ];

  for (const [key, label] of required) {
    if (!draft[key]) {
      return { valid: false, normalizedDraft: draft, message: `${label} is required.` };
    }
    if (isSuspiciousValue(draft[key])) {
      return { valid: false, normalizedDraft: draft, message: `${label} looks invalid. Please enter official data.` };
    }
  }

  if (!isValidPersonName(draft.first_name)) return { valid: false, normalizedDraft: draft, message: "Enter a valid first name." };
  if (!isValidPersonName(draft.last_name)) return { valid: false, normalizedDraft: draft, message: "Enter a valid last name." };
  if (!/^[A-Za-z][A-Za-z &/.-]{1,79}$/.test(draft.company)) return { valid: false, normalizedDraft: draft, message: "Enter a valid company name." };
  if (!/^[A-Za-z][A-Za-z &/.-]{1,59}$/.test(draft.industry)) return { valid: false, normalizedDraft: draft, message: "Enter a valid industry name." };
  if (!/^[A-Za-z][A-Za-z /&-]{1,49}$/.test(draft.city)) return { valid: false, normalizedDraft: draft, message: "Enter a valid city name." };
  if (!/^[A-Za-z][A-Za-z .'-]{1,49}$/.test(draft.state)) return { valid: false, normalizedDraft: draft, message: "Enter a valid state name." };
  if (!/^[A-Za-z][A-Za-z .'-]{1,49}$/.test(draft.country)) return { valid: false, normalizedDraft: draft, message: "Enter a valid country name." };
  if (draft.address.length < 10) return { valid: false, normalizedDraft: draft, message: "Address should be at least 10 characters." };
  if (!/^[A-Za-z0-9 -]{4,10}$/.test(draft.pin)) return { valid: false, normalizedDraft: draft, message: "Enter a valid PIN / ZIP code." };
  if (!isValidEmail(draft.company_email)) return { valid: false, normalizedDraft: draft, message: "Company Email is invalid." };
  if (!hasCorporateDomain(draft.company_email)) return { valid: false, normalizedDraft: draft, message: "Use an official company email domain (not personal email)." };
  if (draft.alt_phone && !/^\+?[0-9\s()-]{10,15}$/.test(draft.alt_phone)) return { valid: false, normalizedDraft: draft, message: "Alternate Phone must be 10 to 15 digits." };
  if (!isValidHttpUrl(draft.website)) return { valid: false, normalizedDraft: draft, message: "Website URL is invalid." };
  if (!isValidHttpUrl(draft.linkedin) || !/linkedin\.com/i.test(draft.linkedin)) {
    return { valid: false, normalizedDraft: draft, message: "LinkedIn URL must be a valid linkedin.com profile/company link." };
  }
  if (draft.company_logo && !isValidHttpUrl(draft.company_logo)) return { valid: false, normalizedDraft: draft, message: "Logo URL is invalid." };

  const year = Number(draft.year_established);
  if (!Number.isInteger(year) || year < 1800 || year > currentYear) {
    return { valid: false, normalizedDraft: draft, message: `Year Established must be between 1800 and ${currentYear}.` };
  }

  const openPositions = Number(draft.open_positions);
  if (!Number.isInteger(openPositions) || openPositions < 0 || openPositions > 50000) {
    return { valid: false, normalizedDraft: draft, message: "Open Positions must be a valid number between 0 and 50000." };
  }

  if (draft.branches) {
    const branches = Number(draft.branches);
    if (!Number.isInteger(branches) || branches < 0 || branches > 10000) {
      return { valid: false, normalizedDraft: draft, message: "Branches must be a valid number between 0 and 10000." };
    }
  }

  if (!["Immediate", "High", "Medium", "Low"].includes(draft.urgency)) {
    return { valid: false, normalizedDraft: draft, message: "Select a valid Hiring Urgency." };
  }

  const bioWords = draft.hiring_bio.split(/\s+/).filter(Boolean).length;
  if (draft.hiring_bio.length < 60 || bioWords < 10) {
    return { valid: false, normalizedDraft: draft, message: "About / Hiring Bio should be at least 60 characters and 10 words." };
  }

  const employmentTypes = toList(draft.employment_types);
  const experienceLevels = toList(draft.experience_levels);
  if (!employmentTypes.length) return { valid: false, normalizedDraft: draft, message: "Add at least one Employment Type." };
  if (!experienceLevels.length) return { valid: false, normalizedDraft: draft, message: "Add at least one Experience Level." };

  if (draft.gst && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(draft.gst)) {
    return { valid: false, normalizedDraft: draft, message: "GST Number is invalid. Use a valid 15-character GSTIN." };
  }

  return { valid: true, normalizedDraft: draft, message: "" };
}

const SECTIONS = [
  { key: "about", label: "About", icon: "🏢" },
  { key: "contact", label: "Contact", icon: "📞" },
  { key: "hiring", label: "Hiring", icon: "🎯" },
  { key: "jobs", label: "Jobs", icon: "💼" },
  { key: "salaries", label: "Salaries", icon: "💰" },
  { key: "interviews", label: "Interviews", icon: "🤝" },
  { key: "benefits", label: "Benefits", icon: "🎁" },
  { key: "photos", label: "Photos", icon: "📸" },
  { key: "posts", label: "Posts", icon: "📢" },
];

/* ─── Normalise job for JobCard ───────────────────────────────────────────── */
function normaliseJob(raw) {
  let tags = [];
  try {
    const rt = raw.skills || raw.tags || "";
    tags = typeof rt === "string"
      ? rt.startsWith("[") ? JSON.parse(rt) : rt.split(",").map((t) => t.trim()).filter(Boolean)
      : Array.isArray(rt) ? rt : [];
  } catch { tags = []; }

  const locLower = (raw.location || "").toLowerCase();
  const locationType = locLower.includes("hybrid") ? "Hybrid" : locLower.includes("remote") ? "Remote" : "On-site";
  const salaryMin = raw.min_salary || 0;
  const salaryMax = raw.max_salary || 0;
  const sMin = Math.round(salaryMin / 100000);
  const sMax = Math.round(salaryMax / 100000);
  const salaryAvg = sMin && sMax ? Math.round((sMin + sMax) / 2) : sMin || sMax;
  const salaryDisplay = (() => {
    const r = (raw.salary || "").replace(/^[$?₹\u20B9\s]+/, "").trim();
    if (r) return `₹${r}`;
    if (sMin && sMax) return `₹${sMin}–${sMax} LPA`;
    if (salaryAvg) return `₹${salaryAvg} LPA`;
    return "Not disclosed";
  })();
  const postedAt = raw.posted_date || raw.created_at || raw.posted_at || null;

  return {
    id: raw.id || raw._id,
    title: raw.title || "Untitled",
    company: raw.company_name || raw.company || "—",
    dept: raw.category || raw.department || "General",
    type: raw.type || raw.job_type || "Full-time",
    location: raw.location || "",
    locationType,
    salaryMin, salaryMax, salaryAvg,
    salary: salaryDisplay,
    tags,
    status: raw.status || "active",
    postedAt,
    postedLabel: formatPostedDate(postedAt),
    isNew: isNew(postedAt),
    description: raw.description || "",
    experience: raw.experience || "",
    openings: Number(raw.openings || raw.vacancies || 1),
    applicantCount: Number(raw.applicant_count || 0),
    recruiter_id: raw.recruiter_id || raw.employer_id || null,
    recruiter_email: raw.recruiter_email || raw.employer_email || null,
    logo: resolveLogoSrc(raw.company_logo || raw.ep_logo || raw.logo || ""),
  };
}

/* ─── Apply helpers ──────────────────────────────────────────────────────── */
async function submitApplicationAPI(job) {
  const res = await candidateFetch("/applications", {
    method: "POST",
    body: JSON.stringify({
      job_id: job.id,
      recruiter_id: job.recruiter_id,
      applied_at: new Date().toISOString(),
    }),
  });
  let data = {};
  try { data = await res.json(); } catch {}
  if (res.status === 409) return { success: true, alreadyApplied: true, application: { job_title: job.title, company: job.company } };
  if (res.status === 422 && data.profileIncomplete) return { success: false, profileIncomplete: true, missing: data.missing || [], error: data.error };
  if (!res.ok) throw new Error(data.detail || data.error || data.message || `Server error ${res.status}`);
  return {
    success: true,
    alreadyApplied: false,
    application: {
      job_title: data.application?.job_title || data.job_title || job.title,
      company: data.application?.company || data.company || job.company,
      id: data.application?.id || data.id || null,
    },
  };
}

function loadPersistedIds(key) {
  try { const r = localStorage.getItem(key); return r ? new Set(JSON.parse(r)) : new Set(); }
  catch { return new Set(); }
}
function persistIds(key, set) {
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch {}
}

/* ─── Success Toast ──────────────────────────────────────────────────────── */
function SuccessToast({ open, job, onClose }) {
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [open, onClose]);
  if (!open || !job) return null;
  return (
    <div style={{
      position: "fixed", bottom: 28, right: 28, zIndex: 9999,
      background: "#fff", border: "1.5px solid #bbf7d0",
      borderRadius: 16, padding: "18px 22px",
      boxShadow: "0 8px 40px rgba(0,0,0,0.14)",
      display: "flex", alignItems: "center", gap: 14, maxWidth: 380,
      animation: "slideInToast 0.35s cubic-bezier(.22,1,.36,1)",
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: "50%",
        background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 20, flexShrink: 0,
      }}>🎉</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#15803d", marginBottom: 3 }}>Application Submitted!</div>
        <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.4 }}>
          Applied to <strong>{job.title}</strong> at <strong>{job.company}</strong>
        </div>
      </div>
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 18, padding: 0 }}>✕</button>
    </div>
  );
}

/* ─── Error Toast ────────────────────────────────────────────────────────── */
function ErrorToast({ open, message, onClose }) {
  useEffect(() => { if (open) { const t = setTimeout(onClose, 7000); return () => clearTimeout(t); } }, [open, onClose]);
  if (!open) return null;
  return (
    <div style={{
      position: "fixed", bottom: 28, right: 28, zIndex: 9999,
      background: "#fef2f2", border: "1.5px solid #fca5a5", borderRadius: 14,
      padding: "14px 18px", boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
      display: "flex", alignItems: "center", gap: 10, maxWidth: 400,
      fontSize: 14, color: "#b91c1c", animation: "slideInToast 0.3s ease",
    }}>
      <span style={{ fontSize: 18 }}>⚠️</span>
      <span style={{ flex: 1, lineHeight: 1.4 }}>{message || "Something went wrong."}</span>
      <button onClick={onClose} style={{ background: "none", border: "none", color: "#b91c1c", cursor: "pointer", fontSize: 18 }}>✕</button>
    </div>
  );
}

/* ─── Job Card (same style as JobsPage) ─────────────────────────────────── */
function RecruiterJobCard({ job, appliedJobs, applyingJobId, onApplyClick, onViewDesc, index }) {
  const isApplied = appliedJobs.has(job.id);
  const isApplying = applyingJobId === job.id;
  const color = companyColor(job.company);
  const initial = (job.company || "?")[0].toUpperCase();

  return (
    <div
      className="rp-jc"
      data-aos="fade-up"
      data-aos-delay={index * 60}
      style={{ "--acc": color }}
    >
      <div className="rp-jc__stripe" />
      <div className="rp-jc__header">
        <div className="rp-jc__logo" style={{ background: `${color}14`, borderColor: `${color}30` }}>
          {job.logo
            ? <img src={job.logo} alt={job.company} style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 8 }} />
            : <span style={{ color, fontWeight: 900, fontSize: "1.05rem" }}>{initial}</span>}
        </div>
        <div className="rp-jc__header-info">
          <div className="rp-jc__company-name">{job.company}</div>
          {job.postedLabel && (
            <div className={`rp-jc__posted${job.isNew ? " rp-jc__posted--new" : ""}`}>
              {job.isNew && <span className="rp-jc__new-dot" />}
              {job.postedLabel}
            </div>
          )}
        </div>
        {isApplied
          ? <span className="rp-jc__status rp-jc__status--applied">✓ Applied</span>
          : job.status === "active"
          ? <span className="rp-jc__status rp-jc__status--active">● Hiring</span>
          : <span className="rp-jc__status rp-jc__status--paused">⏸ Paused</span>}
      </div>

      <h3 className="rp-jc__title">{job.title}</h3>
      <span className="rp-jc__dept" style={{ color, background: `${color}12`, borderColor: `${color}25` }}>{job.dept}</span>

      <div className="rp-jc__meta">
        {job.location && (
          <span className="rp-jc__chip rp-jc__chip--loc">📍 {job.location}</span>
        )}
        <span className="rp-jc__chip">{job.type}</span>
        {job.locationType !== "On-site" && (
          <span className={`rp-jc__chip${job.locationType === "Remote" ? " rp-jc__chip--remote" : " rp-jc__chip--hybrid"}`}>
            {job.locationType === "Remote" ? "🌐 Remote" : "🔄 Hybrid"}
          </span>
        )}
        {job.experience && <span className="rp-jc__chip">🎓 {job.experience}</span>}
      </div>

      {job.salary !== "Not disclosed" && (
        <div className="rp-jc__salary" style={{ color }}>
          {job.salary}
        </div>
      )}

      {job.tags.length > 0 && (
        <div className="rp-jc__tags">
          {job.tags.slice(0, 4).map((t, i) => <span key={i} className="rp-jc__tag">{t}</span>)}
          {job.tags.length > 4 && <span className="rp-jc__tag rp-jc__tag--more">+{job.tags.length - 4}</span>}
        </div>
      )}

      <div className="rp-jc__info-bar">
        <span className="rp-jc__vacancy">👥 {job.openings === 1 ? "1 vacancy" : `${job.openings} vacancies`}</span>
      </div>

      <div className="rp-jc__actions">
        <button className="rp-jc__view" onClick={() => onViewDesc(job)}>
          👁 View Details
        </button>
        {isApplied ? (
          <button className="rp-jc__apply rp-jc__apply--done" disabled>✓ Applied</button>
        ) : isApplying ? (
          <button className="rp-jc__apply rp-jc__apply--applying" disabled>
            <span className="rp-spinner" />Applying…
          </button>
        ) : (
          <button className="rp-jc__apply" onClick={() => onApplyClick(job)} disabled={job.status === "paused"}>
            Apply Now →
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Job Detail Modal ───────────────────────────────────────────────────── */
function JobDetailModal({ job, onClose, appliedJobs, applyingJobId, onApplyClick }) {
  const isApplied = appliedJobs.has(job.id);
  const isApplying = applyingJobId === job.id;
  const color = companyColor(job.company);

  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", fn); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <div className="rp-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rp-modal" role="dialog" aria-modal="true">
        <div className="rp-modal__head" style={{ "--acc": color }}>
          <div className="rp-modal__logo" style={{ background: `${color}15`, borderColor: `${color}30` }}>
            {job.logo
              ? <img src={job.logo} alt={job.company} style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "inherit" }} />
              : <span style={{ color, fontWeight: 900, fontSize: "1.4rem" }}>{(job.company || "?")[0]}</span>}
          </div>
          <div className="rp-modal__head-text">
            <div className="rp-modal__title">{job.title}</div>
            <div className="rp-modal__company">{job.company}</div>
          </div>
          <button className="rp-modal__close" onClick={onClose}>✕</button>
        </div>
        <div className="rp-modal__body">
          <div className="rp-modal__chips">
            <span className="rp-modal__chip" style={{ color, background: `${color}12` }}>{job.dept}</span>
            <span className="rp-modal__chip">{job.type}</span>
            {job.location && <span className="rp-modal__chip">📍 {job.location}</span>}
            {job.experience && <span className="rp-modal__chip">🎓 {job.experience}</span>}
            {job.locationType !== "On-site" && <span className="rp-modal__chip">{job.locationType}</span>}
          </div>

          <div className="rp-modal__meta-row">
            <div className="rp-modal__meta-item">
              <div className="rp-modal__meta-icon" style={{ background: color + "18", color }}>👥</div>
              <div>
                <div className="rp-modal__meta-label">Vacancies</div>
                <div className="rp-modal__meta-val" style={{ color }}>{job.openings === 1 ? "1 opening" : `${job.openings} openings`}</div>
              </div>
            </div>
            {job.salary !== "Not disclosed" && (
              <div className="rp-modal__meta-item">
                <div className="rp-modal__meta-icon" style={{ background: "#f0fdf4", color: "#16a34a" }}>💰</div>
                <div>
                  <div className="rp-modal__meta-label">Salary</div>
                  <div className="rp-modal__meta-val" style={{ color: "#16a34a" }}>{job.salary}</div>
                </div>
              </div>
            )}
            {job.postedLabel && (
              <div className="rp-modal__meta-item">
                <div className="rp-modal__meta-icon" style={{ background: "#f0f0f8", color: "#6b7280" }}>🕐</div>
                <div>
                  <div className="rp-modal__meta-label">Posted</div>
                  <div className="rp-modal__meta-val">{job.postedLabel}</div>
                </div>
              </div>
            )}
          </div>

          {job.description && (
            <div className="rp-modal__section">
              <div className="rp-modal__section-title">About this role</div>
              <div className="rp-modal__desc">{job.description}</div>
            </div>
          )}
          {job.tags.length > 0 && (
            <div className="rp-modal__section">
              <div className="rp-modal__section-title">Skills & Technologies</div>
              <div className="rp-modal__tags">
                {job.tags.map((t, i) => <span key={i} className="rp-modal__tag">{t}</span>)}
              </div>
            </div>
          )}
        </div>
        <div className="rp-modal__footer">
          {isApplied ? (
            <button className="rp-modal__apply rp-modal__apply--done" disabled>✓ Already Applied</button>
          ) : isApplying ? (
            <button className="rp-modal__apply" disabled style={{ opacity: 0.75 }}>
              <span className="rp-spinner" style={{ marginRight: 8 }} />Applying…
            </button>
          ) : (
            <button className="rp-modal__apply" onClick={() => { onClose(); onApplyClick(job); }}>Apply Now →</button>
          )}
          <button className="rp-modal__close-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Profile Incomplete Popup ───────────────────────────────────────────── */
function ProfileIncompletePopup({ open, onClose, targetJob }) {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  if (!open) return null;
  return (
    <div className="rp-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rp-pip-modal" role="dialog">
        <div className="rp-pip__accent" />
        <div className="rp-pip__icon">👤</div>
        <h3 className="rp-pip__title">Profile Incomplete</h3>
        <p className="rp-pip__sub">
          {targetJob
            ? <>To apply for <strong>{targetJob.title}</strong>, complete your profile first.</>
            : "Complete your profile to start applying for jobs."}
        </p>
        <div className="rp-pip__actions">
          <a href="/employee/profile" className="rp-modal__apply">Complete Profile →</a>
          <button className="rp-modal__close-btn" onClick={onClose}>Maybe Later</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */
export default function RecruiterProfile() {
  const navigate = useNavigate();
  useScrollReveal();

  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [activeSection, setActiveSection] = useState("about");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(buildDraft());
  const [sectionDraft, setSectionDraft] = useState(buildSectionDraft());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Apply state
  const [appliedJobs, setAppliedJobs] = useState(() => loadPersistedIds("jp_applied"));
  const [applyingJobId, setApplyingJobId] = useState(null);
  const applyingRef = useRef(new Set());
  const [pendingJob, setPendingJob] = useState(null);
  const [profilePopupOpen, setProfilePopupOpen] = useState(false);
  const [successToast, setSuccessToast] = useState({ open: false, job: null });
  const [errorToast, setErrorToast] = useState({ open: false, message: "" });

  useEffect(() => { persistIds("jp_applied", appliedJobs); }, [appliedJobs]);
  useEffect(() => { if (formError) setFormError(""); }, [draft, sectionDraft]);

  /* Load profile + jobs */
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        const [pRes, jRes] = await Promise.all([
          authFetch("/api/employer/profile"),
          authFetch("/api/employer/jobs"),
        ]);
        const pData = await pRes.json();
        const jData = await jRes.json();
        const allJobs = Array.isArray(jData) ? jData : (jData.data || []);
        if (!mounted) return;
        if (!pRes.ok) throw new Error(pData.error || "Could not load profile.");
        setProfile(pData);
        setDraft(buildDraft(pData));
        setSectionDraft(buildSectionDraft(pData));
        setJobs(allJobs.filter((j) => String(j.employer_id) === String(pData.id)).map(normaliseJob));
      } catch (err) {
        if (mounted) setError(err.message || "Could not load profile.");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  /* Save profile */
  async function saveProfile() {
    const { valid, normalizedDraft, message } = validateCompanyProfileDraft(draft);
    if (!valid) {
      setFormError(message || "Please fill all required fields correctly.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setFormError("");
      const payload = {
        ...normalizedDraft,
        employment_types: JSON.stringify(toList(normalizedDraft.employment_types)),
        experience_levels: JSON.stringify(toList(normalizedDraft.experience_levels)),
        public_sections: JSON.stringify(sectionDraft),
        logo_url: normalizedDraft.company_logo,
      };
      const res = await authFetch("/api/employer/profile", { method: "PUT", body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not update profile.");
      setDraft((prev) => ({ ...prev, ...normalizedDraft }));
      setProfile((prev) => ({ ...(prev || {}), ...normalizedDraft, public_sections: sectionDraft }));
      setIsEditing(false);
    } catch (err) {
      setError(err.message || "Could not update profile.");
    } finally {
      setSaving(false);
    }
  }

  /* Apply flow */
  const handleApplyClick = useCallback(async (job) => {
    if (appliedJobs.has(job.id)) return;
    const candidateUser = readCandidateUser();
    if (!candidateUser) {
      window.location.href = "/employee/login";
      return;
    }
    if (applyingRef.current.has(job.id)) return;
    applyingRef.current.add(job.id);
    setApplyingJobId(job.id);
    try {
      const result = await submitApplicationAPI(job);
      if (result.alreadyApplied) {
        setAppliedJobs((prev) => { const n = new Set([...prev, job.id]); persistIds("jp_applied", n); return n; });
        setSuccessToast({ open: true, job: { title: job.title, company: job.company } });
        return;
      }
      if (result.profileIncomplete) {
        setPendingJob(job);
        setProfilePopupOpen(true);
        return;
      }
      if (!result.success) { setErrorToast({ open: true, message: result.error || "Application failed." }); return; }
      setAppliedJobs((prev) => { const n = new Set([...prev, job.id]); persistIds("jp_applied", n); return n; });
      setSuccessToast({ open: true, job: { title: job.title, company: job.company } });
    } catch (err) {
      setErrorToast({ open: true, message: err.message || "Could not submit application." });
    } finally {
      applyingRef.current.delete(job.id);
      setApplyingJobId(null);
    }
  }, [appliedJobs]);

  /* Computed values */
  const metrics = useMemo(() => {
    const activeJobsList = jobs.filter((j) => String(j.status).toLowerCase() === "active");
    const salaries = jobs.flatMap((j) => [j.salaryMin, j.salaryMax].filter(Boolean));
    const avgSalary = salaries.length ? Math.round(salaries.reduce((s, v) => s + v, 0) / salaries.length) : null;
    return [
      { label: "Company", value: profile?.company || "Not set", icon: "🏢" },
      { label: "Industry", value: profile?.industry || "Not set", icon: "🏭" },
      { label: "Location", value: profile?.city || "Not set", icon: "📍" },
      { label: "Active Jobs", value: activeJobsList.length, icon: "💼" },
      { label: "Avg Salary", value: avgSalary ? formatCurrency(avgSalary) : "Not shared", icon: "💰" },
      { label: "Status", value: profile?.status || "pending", icon: "✅" },
    ];
  }, [jobs, profile]);

  const profileCompletion = useMemo(() => {
    const checks = [draft.company, draft.industry, draft.city, draft.state, draft.country, draft.size, draft.website, draft.linkedin, draft.address, draft.company_logo, draft.hiring_bio, draft.role, draft.dept, draft.company_email, draft.alt_phone, draft.urgency, draft.open_positions, draft.year_established];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [draft]);

  const displayName = profile?.company || `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() || "Recruiter Profile";
  const companyLogo = resolveLogoSrc(profile?.company_logo || profile?.logo_url || "");
  const website = profile?.website || "";
  const linkedin = profile?.linkedin || "";
  const contactEmail = profile?.company_email || profile?.email || "";
  const contactPhone = profile?.phone || "";
  const aboutText = profile?.hiring_bio || profile?.notes || "Share your company story, work culture, and hiring journey here.";
  const summaryTags = [draft.company_type || profile?.company_type, draft.size || profile?.size, draft.country || profile?.country, draft.urgency || profile?.urgency].filter(Boolean);
  const activeJobs = jobs.filter((j) => String(j.status).toLowerCase() === "active");

  const setSectionField = (section, key, value) => {
    setSectionDraft((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), [key]: value } }));
  };
  const activeDraft = sectionDraft[activeSection] || {};

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700;800&display=swap');

        /* ── AOS animations ── */
        [data-aos] {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.55s cubic-bezier(.22,1,.36,1), transform 0.55s cubic-bezier(.22,1,.36,1);
        }
        [data-aos="fade-up"] { opacity: 0; transform: translateY(28px); }
        [data-aos="fade-right"] { opacity: 0; transform: translateX(-28px); }
        [data-aos="fade-left"] { opacity: 0; transform: translateX(28px); }
        [data-aos="zoom-in"] { opacity: 0; transform: scale(0.94); }
        [data-aos].aos-animate { opacity: 1; transform: none; }
        [data-aos-delay="60"].aos-animate  { transition-delay: 0.06s; }
        [data-aos-delay="120"].aos-animate { transition-delay: 0.12s; }
        [data-aos-delay="180"].aos-animate { transition-delay: 0.18s; }
        [data-aos-delay="240"].aos-animate { transition-delay: 0.24s; }
        [data-aos-delay="300"].aos-animate { transition-delay: 0.30s; }
        [data-aos-delay="360"].aos-animate { transition-delay: 0.36s; }
        [data-aos-delay="100"].aos-animate { transition-delay: 0.10s; }
        [data-aos-delay="150"].aos-animate { transition-delay: 0.15s; }
        [data-aos-delay="200"].aos-animate { transition-delay: 0.20s; }

        @keyframes slideInToast {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: none; }
        }
        @keyframes spinAnim {
          to { transform: rotate(360deg); }
        }
        @keyframes heroPulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.08); opacity: 0.85; }
        }
        @keyframes progressFill {
          from { width: 0; }
          to { width: var(--pct); }
        }

        /* ── Root ── */
        .rp-root {
          font-family: 'DM Sans', sans-serif;
          min-height: 100vh;
          background: #f8f9fb;
          color: #1a1a2e;
          font-size: 15px;
          line-height: 1.65;
        }

        /* ── Hero ── */
        .rp-hero {
          position: relative;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          color: #fff;
          padding: 0;
          overflow: hidden;
        }
        .rp-hero::before {
          content: '';
          position: absolute; inset: 0;
          background:
            radial-gradient(circle at 20% 50%, rgba(99,102,241,0.3) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(16,185,129,0.2) 0%, transparent 40%);
          pointer-events: none;
        }
        .rp-hero__inner {
          position: relative; z-index: 1;
          padding: clamp(20px, 4vw, 40px) clamp(20px, 5vw, 60px);
        }
        .rp-hero__topbar {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; flex-wrap: wrap; margin-bottom: 32px;
        }
        .rp-back-btn {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2);
          color: #fff; border-radius: 10px; padding: 10px 18px;
          font-size: 14px; font-weight: 400; cursor: pointer;
          transition: background 0.2s, transform 0.2s;
          font-family: inherit;
        }
        .rp-back-btn:hover { background: rgba(255,255,255,0.2); transform: translateX(-2px); }
        .rp-hero__actions { display: flex; gap: 10px; flex-wrap: wrap; }
        .rp-action-btn {
          display: inline-flex; align-items: center; gap: 6px;
          background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2);
          color: #fff; border-radius: 10px; padding: 10px 16px;
          font-size: 14px; font-weight: 400; cursor: pointer;
          transition: all 0.2s; font-family: inherit;
        }
        .rp-action-btn:hover { background: rgba(255,255,255,0.22); transform: translateY(-1px); }
        .rp-action-btn--primary {
          background: linear-gradient(135deg, #6366f1, #394420);
          border-color: transparent;
        }
        .rp-action-btn--primary:hover { opacity: 0.9; }

        .rp-hero__main {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 32px; align-items: start;
        }
        .rp-brand { display: flex; align-items: center; gap: 22px; }
        .rp-brand__logo-wrap {
          position: relative; flex-shrink: 0;
        }
        .rp-brand__logo, .rp-brand__initials {
          width: 100px; height: 100px; border-radius: 22px;
          border: 2px solid rgba(255,255,255,0.2);
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
        }
        .rp-brand__logo { object-fit: cover; background: #fff; }
        .rp-brand__initials {
          display: grid; place-items: center;
          background: linear-gradient(135deg, #6366f1, #394420);
          color: #fff; font-size: 2.2rem; font-weight: 400;
          font-family: 'DM Serif Display', serif;
        }
        .rp-brand__verified {
          position: absolute; bottom: -4px; right: -4px;
          width: 24px; height: 24px; border-radius: 50%;
          background: #263012; border: 2px solid #1a1a2e;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px;
        }
        .rp-brand__info { flex: 1; }
        .rp-brand__kicker {
          font-size: 11px; text-transform: uppercase; letter-spacing: 0.15em;
          color: rgba(255,255,255,0.55); font-weight: 400; margin-bottom: 6px;
        }
        .rp-brand__name {
          font-family: 'DM Serif Display', serif;
          font-size: clamp(1.8rem, 4vw, 3rem);
          font-weight: 400; line-height: 1.1; margin: 0 0 8px;
          color: #fff;
        }
        .rp-brand__subtitle {
          font-size: 14px; color: rgba(255,255,255,0.65);
          max-width: 55ch; line-height: 1.6; margin: 0 0 14px;
        }
        .rp-tag-row { display: flex; flex-wrap: wrap; gap: 8px; }
        .rp-tag {
          padding: 5px 12px; border-radius: 8px;
          background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2);
          color: rgba(255,255,255,0.9); font-size: 12px; font-weight: 400;
        }

        /* ── Metric cards ── */
        .rp-metrics {
          display: grid; grid-template-columns: repeat(3, 120px); gap: 10px;
          flex-shrink: 0;
        }
        .rp-metric {
          background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12);
          border-radius: 16px; padding: 14px 12px; text-align: center;
          backdrop-filter: blur(8px);
          transition: background 0.2s, transform 0.2s;
        }
        .rp-metric:hover { background: rgba(255,255,255,0.14); transform: translateY(-2px); }
        .rp-metric__icon { font-size: 20px; margin-bottom: 6px; }
        .rp-metric__label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(255,255,255,0.5); margin-bottom: 4px; }
        .rp-metric__val { font-size: 13px; font-weight: 400; color: #fff; word-break: break-word; }

        /* ── Shell ── */
        .rp-shell {
          display: grid; grid-template-columns: 260px 1fr;
          gap: 24px; padding: 28px clamp(20px, 5vw, 60px) 60px;
          max-width: 1400px; margin: 0 auto;
        }

        /* ── Sidebar ── */
        .rp-sidebar {
          display: flex; flex-direction: column; gap: 16px;
          position: sticky; top: 24px; align-self: start;
          max-height: calc(100vh - 48px); overflow-y: auto;
        }
        .rp-sidebar::-webkit-scrollbar { width: 4px; }
        .rp-sidebar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }

        .rp-sidebar__card {
          background: #fff; border: 1px solid #e8ecf0; border-radius: 20px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.04); overflow: hidden;
        }
        .rp-sidebar__card-title {
          font-size: 11px; text-transform: uppercase; letter-spacing: 0.12em;
          color: #94a3b8; font-weight: 400; padding: 16px 16px 10px;
          border-bottom: 1px solid #f1f5f9;
        }

        .rp-nav-btn {
          display: flex; align-items: center; gap: 10px;
          width: 100%; padding: 12px 16px; text-align: left;
          background: none; border: none; border-bottom: 1px solid #f8fafc;
          font-family: inherit; font-size: 14px; font-weight: 400;
          color: #475569; cursor: pointer; transition: all 0.2s;
        }
        .rp-nav-btn:last-child { border-bottom: none; }
        .rp-nav-btn:hover { background: #f8fafc; color: #1a1a2e; }
        .rp-nav-btn.is-active {
          background: linear-gradient(135deg, #eef2ff, #f0fdf4);
          color: #4338ca; font-weight: 400;
          border-left: 3px solid #6366f1;
        }
        .rp-nav-btn__icon { font-size: 16px; }
        .rp-nav-btn__count {
          margin-left: auto; background: #e0e7ff; color: #4338ca;
          font-size: 11px; font-weight: 400; padding: 2px 7px; border-radius: 20px;
        }

        .rp-sidebar__info { padding: 12px 16px; }
        .rp-sidebar__info-row {
          display: flex; flex-direction: column; gap: 3px;
          padding: 10px 0; border-bottom: 1px solid #f1f5f9;
        }
        .rp-sidebar__info-row:last-child { border-bottom: none; }
        .rp-sidebar__info-row span { font-size: 11px; color: #94a3b8; font-weight: 400; text-transform: uppercase; letter-spacing: 0.08em; }
        .rp-sidebar__info-row strong { font-size: 13px; color: #1e293b; font-weight: 400; word-break: break-word; }

        .rp-progress-wrap { padding: 14px 16px 16px; }
        .rp-progress__label { font-size: 12px; font-weight: 400; color: #475569; margin-bottom: 8px; display: flex; justify-content: space-between; }
        .rp-progress__track { height: 6px; background: #e2e8f0; border-radius: 999px; overflow: hidden; }
        .rp-progress__fill {
          height: 100%; border-radius: 999px;
          background: linear-gradient(90deg, #6366f1, #394420);
          animation: progressFill 1.2s cubic-bezier(.22,1,.36,1) both;
          --pct: 0%;
        }

        /* ── Content ── */
        .rp-content { min-height: 400px; display: flex; flex-direction: column; gap: 20px; }

        /* ── Cards ── */
        .rp-card {
          background: #fff; border: 1px solid #e8ecf0; border-radius: 24px;
          box-shadow: 0 2px 16px rgba(0,0,0,0.04); overflow: hidden;
        }
        .rp-card__head {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; padding: 22px 24px 18px; border-bottom: 1px solid #f1f5f9;
          flex-wrap: wrap;
        }
        .rp-card__head-left { display: flex; align-items: center; gap: 12px; }
        .rp-card__icon {
          width: 40px; height: 40px; border-radius: 12px;
          background: linear-gradient(135deg, #eef2ff, #e0e7ff);
          display: flex; align-items: center; justify-content: center;
          font-size: 18px;
        }
        .rp-card__head h2 {
          font-family: 'DM Serif Display', serif;
          font-size: 1.4rem; font-weight: 400; margin: 0; color: #1a1a2e;
        }
        .rp-card__body { padding: 22px 24px; }
        .rp-card__actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

        .rp-pill {
          padding: 5px 12px; border-radius: 8px;
          background: #f0fdf4; color: #15803d;
          font-size: 12px; font-weight: 400;
        }
        .rp-pill--warn { background: #fef3c7; color: #92400e; }
        .rp-pill--blue { background: #eff6ff; color: #1d4ed8; }
        .rp-pill--purple { background: #f5f3ff; color: #6d28d9; }

        .rp-edit-btn {
          padding: 8px 16px; border-radius: 10px;
          background: #f8fafc; border: 1px solid #e2e8f0;
          font-size: 13px; font-weight: 400; color: #475569;
          cursor: pointer; transition: all 0.2s; font-family: inherit;
        }
        .rp-edit-btn:hover { background: #eef2ff; color: #4338ca; border-color: #c7d2fe; }
        .rp-edit-btn--primary {
          background: linear-gradient(135deg, #6366f1, #394420);
          color: #fff; border-color: transparent;
        }
        .rp-edit-btn--primary:hover { opacity: 0.9; color: #fff; }

        /* ── About view ── */
        .rp-about__text {
          font-size: 15px; line-height: 1.75; color: #475569;
          margin-bottom: 20px;
        }
        .rp-details-grid {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 14px;
        }
        .rp-detail-box {
          padding: 16px; border-radius: 16px;
          background: #f8fafc; border: 1px solid #f1f5f9;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .rp-detail-box:hover { border-color: #c7d2fe; box-shadow: 0 2px 12px rgba(99,102,241,0.08); }
        .rp-detail-box__label {
          font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em;
          color: #94a3b8; font-weight: 400; margin-bottom: 6px;
        }
        .rp-detail-box__val { font-size: 14px; font-weight: 400; color: #1e293b; }

        /* ── Edit form ── */
        .rp-edit-form { padding: 24px; display: flex; flex-direction: column; gap: 18px; }
        .rp-form-note { font-size: 13px; color: #64748b; line-height: 1.6; padding: 12px 16px; background: #f0f9ff; border-radius: 10px; border-left: 3px solid #6366f1; }
        .rp-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 14px; }
        .rp-field { display: grid; gap: 6px; }
        .rp-field__label { font-size: 11px; font-weight: 400; text-transform: uppercase; letter-spacing: 0.1em; color: #6366f1; }
        .rp-field input, .rp-field textarea, .rp-field select {
          width: 100%; border: 1.5px solid #e2e8f0; border-radius: 10px;
          padding: 10px 13px; font-family: inherit; font-size: 14px;
          color: #1a1a2e; background: #fff; outline: none;
          transition: border-color 0.2s, box-shadow 0.2s; box-sizing: border-box;
        }
        .rp-field input:focus, .rp-field textarea:focus, .rp-field select:focus {
          border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
        }
        .rp-field--full { grid-column: 1 / -1; }
        .rp-edit-actions { display: flex; gap: 10px; justify-content: flex-end; flex-wrap: wrap; margin-top: 4px; }

        /* ── Job cards grid ── */
        .rp-jobs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 18px; padding: 24px;
        }

        /* ── Job card (matches JobsPage) ── */
        .rp-jc {
          position: relative; background: #fff; border-radius: 20px;
          border: 1.5px solid #e8ecf0; padding: 20px;
          overflow: hidden; cursor: default;
          transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s;
          display: flex; flex-direction: column; gap: 0;
        }
        .rp-jc:hover { transform: translateY(-3px); box-shadow: 0 12px 40px rgba(0,0,0,0.08); border-color: #c7d2fe; }
        .rp-jc__stripe {
          position: absolute; left: 0; top: 0; bottom: 0; width: 4px;
          background: var(--acc, #6366f1); border-radius: 20px 0 0 20px;
        }
        .rp-jc__header { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 14px; }
        .rp-jc__logo {
          width: 48px; height: 48px; flex-shrink: 0;
          border-radius: 12px; border: 1.5px solid #e8ecf0;
          display: flex; align-items: center; justify-content: center; overflow: hidden;
        }
        .rp-jc__header-info { flex: 1; }
        .rp-jc__company-name { font-size: 13px; font-weight: 400; color: #475569; }
        .rp-jc__posted { font-size: 11px; color: #94a3b8; display: flex; align-items: center; gap: 4px; margin-top: 2px; }
        .rp-jc__posted--new { color: #16a34a; font-weight: 400; }
        .rp-jc__new-dot { width: 6px; height: 6px; border-radius: 50%; background: #16a34a; display: inline-block; }
        .rp-jc__status {
          flex-shrink: 0; font-size: 11px; font-weight: 400; padding: 4px 10px;
          border-radius: 20px;
        }
        .rp-jc__status--active { background: #dcfce7; color: #15803d; }
        .rp-jc__status--applied { background: #eff6ff; color: #1d4ed8; }
        .rp-jc__status--paused { background: #fef3c7; color: #92400e; }
        .rp-jc__title { font-family: 'DM Serif Display', serif; font-size: 1.1rem; font-weight: 400; margin: 0 0 10px; color: #1a1a2e; line-height: 1.3; }
        .rp-jc__dept {
          display: inline-block; font-size: 11px; font-weight: 400;
          padding: 4px 10px; border-radius: 8px; border: 1px solid;
          margin-bottom: 12px;
        }
        .rp-jc__meta { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
        .rp-jc__chip {
          font-size: 11px; font-weight: 400; padding: 4px 10px;
          border-radius: 8px; background: #f8fafc; border: 1px solid #e2e8f0;
          color: #64748b;
        }
        .rp-jc__chip--loc { color: #0369a1; background: #e0f2fe; border-color: #bae6fd; }
        .rp-jc__chip--remote { color: #0891b2; background: #ecfeff; border-color: #a5f3fc; }
        .rp-jc__chip--hybrid { color: #7c3aed; background: #f5f3ff; border-color: #ddd6fe; }
        .rp-jc__salary { font-size: 16px; font-weight: 400; color: var(--acc); margin: 8px 0; }
        .rp-jc__tags { display: flex; flex-wrap: wrap; gap: 6px; margin: 8px 0; }
        .rp-jc__tag { font-size: 11px; padding: 3px 9px; border-radius: 6px; background: #f1f5f9; color: #475569; font-weight: 400; }
        .rp-jc__tag--more { color: #94a3b8; font-style: italic; }
        .rp-jc__info-bar { font-size: 12px; color: #94a3b8; margin: 10px 0 14px; }
        .rp-jc__vacancy { font-weight: 400; }
        .rp-jc__actions { display: flex; gap: 10px; margin-top: auto; padding-top: 4px; }
        .rp-jc__view {
          flex: 1; padding: 10px; border-radius: 10px;
          background: #f8fafc; border: 1.5px solid #e2e8f0;
          font-size: 13px; font-weight: 400; color: #475569;
          cursor: pointer; transition: all 0.2s; font-family: inherit;
          display: flex; align-items: center; justify-content: center; gap: 6px;
        }
        .rp-jc__view:hover { background: #eef2ff; color: #4338ca; border-color: #c7d2fe; }
        .rp-jc__apply {
          flex: 1; padding: 10px; border-radius: 10px;
          background: linear-gradient(135deg, #6366f1, #394420);
          border: none; color: #fff; font-size: 13px; font-weight: 400;
          cursor: pointer; transition: all 0.2s; font-family: inherit;
          display: flex; align-items: center; justify-content: center; gap: 6px;
        }
        .rp-jc__apply:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
        .rp-jc__apply:disabled { opacity: 0.6; cursor: not-allowed; }
        .rp-jc__apply--done { background: linear-gradient(135deg, #d1fae5, #a7f3d0); color: #065f46; }
        .rp-jc__apply--applying { background: #e0e7ff; color: #4338ca; }

        /* ── Spinner ── */
        .rp-spinner {
          display: inline-block; width: 14px; height: 14px;
          border: 2px solid rgba(99,102,241,0.3); border-top-color: #6366f1;
          border-radius: 50%; animation: spinAnim 0.7s linear infinite;
        }

        /* ── List items ── */
        .rp-list { display: flex; flex-direction: column; gap: 10px; }
        .rp-list-item {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 16px; padding: 14px 16px; border-radius: 14px;
          background: #f8fafc; border: 1px solid #f1f5f9;
          transition: border-color 0.2s;
        }
        .rp-list-item:hover { border-color: #c7d2fe; }
        .rp-list-item strong { display: block; font-size: 14px; font-weight: 400; color: #1e293b; margin-bottom: 3px; }
        .rp-list-item p { margin: 0; font-size: 13px; color: #64748b; }

        /* ── Empty / state ── */
        .rp-empty, .rp-state {
          padding: 40px 24px; text-align: center;
          color: #94a3b8; font-size: 14px;
        }
        .rp-empty__icon { font-size: 40px; margin-bottom: 12px; opacity: 0.6; }
        .rp-empty__title { font-size: 16px; font-weight: 400; color: #64748b; margin-bottom: 6px; }
        .rp-state--error { color: #b91c1c; }

        /* ── Modal ── */
        .rp-overlay {
          position: fixed; inset: 0; background: rgba(15,23,42,0.55);
          backdrop-filter: blur(4px); z-index: 9000;
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .rp-modal {
          background: #fff; border-radius: 24px; width: 100%; max-width: 580px;
          max-height: 90vh; overflow-y: auto; box-shadow: 0 24px 80px rgba(0,0,0,0.2);
          animation: slideUp 0.3s cubic-bezier(.22,1,.36,1);
        }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: none; } }
        .rp-modal__head {
          display: flex; align-items: center; gap: 14px;
          padding: 20px 22px; border-bottom: 1px solid #f1f5f9;
          background: linear-gradient(135deg, #f8fafc, #f0f4ff);
          position: relative;
        }
        .rp-modal__head::before {
          content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px;
          background: var(--acc); border-radius: 24px 0 0 0;
        }
        .rp-modal__logo {
          width: 56px; height: 56px; border-radius: 14px; flex-shrink: 0;
          border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: center; overflow: hidden;
        }
        .rp-modal__head-text { flex: 1; }
        .rp-modal__title { font-family: 'DM Serif Display', serif; font-size: 1.2rem; color: #1a1a2e; }
        .rp-modal__company { font-size: 13px; color: #64748b; font-weight: 400; }
        .rp-modal__close {
          background: #f1f5f9; border: none; color: #64748b;
          width: 32px; height: 32px; border-radius: 8px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; transition: all 0.2s;
        }
        .rp-modal__close:hover { background: #fee2e2; color: #394420; }
        .rp-modal__body { padding: 22px; }
        .rp-modal__chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
        .rp-modal__chip { padding: 5px 12px; border-radius: 8px; background: #f1f5f9; font-size: 12px; font-weight: 400; color: #475569; }
        .rp-modal__meta-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 12px; margin-bottom: 20px; }
        .rp-modal__meta-item { display: flex; align-items: center; gap: 10px; }
        .rp-modal__meta-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
        .rp-modal__meta-label { font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 400; }
        .rp-modal__meta-val { font-size: 14px; font-weight: 400; color: #1e293b; }
        .rp-modal__section { margin-bottom: 18px; }
        .rp-modal__section-title { font-size: 13px; font-weight: 400; color: #1e293b; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 10px; }
        .rp-modal__desc { font-size: 14px; color: #475569; line-height: 1.7; white-space: pre-wrap; }
        .rp-modal__tags { display: flex; flex-wrap: wrap; gap: 8px; }
        .rp-modal__tag { padding: 5px 12px; border-radius: 8px; background: #eef2ff; color: #4338ca; font-size: 12px; font-weight: 400; }
        .rp-modal__footer { display: flex; gap: 10px; padding: 18px 22px; border-top: 1px solid #f1f5f9; flex-wrap: wrap; }
        .rp-modal__apply {
          flex: 1; padding: 12px 20px; border-radius: 12px;
          background: linear-gradient(135deg, #6366f1, #394420);
          border: none; color: #fff; font-size: 14px; font-weight: 400;
          cursor: pointer; transition: all 0.2s; font-family: inherit;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          text-decoration: none; text-align: center;
        }
        .rp-modal__apply:hover:not(:disabled) { opacity: 0.9; }
        .rp-modal__apply:disabled { opacity: 0.6; cursor: not-allowed; }
        .rp-modal__apply--done { background: linear-gradient(135deg, #d1fae5, #a7f3d0); color: #065f46; }
        .rp-modal__close-btn {
          padding: 12px 20px; border-radius: 12px;
          background: #f8fafc; border: 1.5px solid #e2e8f0;
          font-size: 14px; font-weight: 400; color: #64748b;
          cursor: pointer; font-family: inherit; transition: all 0.2s;
        }
        .rp-modal__close-btn:hover { background: #f1f5f9; }

        /* ── Profile incomplete popup ── */
        .rp-pip-modal {
          background: #fff; border-radius: 24px; width: 100%; max-width: 400px;
          padding: 32px; text-align: center; position: relative; overflow: hidden;
          animation: slideUp 0.3s cubic-bezier(.22,1,.36,1);
        }
        .rp-pip__accent {
          position: absolute; top: 0; left: 0; right: 0; height: 4px;
          background: linear-gradient(90deg, #6366f1, #394420, #ec4899);
        }
        .rp-pip__icon { font-size: 48px; margin-bottom: 16px; }
        .rp-pip__title { font-family: 'DM Serif Display', serif; font-size: 1.4rem; margin-bottom: 10px; color: #1a1a2e; }
        .rp-pip__sub { font-size: 14px; color: #64748b; line-height: 1.6; margin-bottom: 24px; }
        .rp-pip__actions { display: flex; flex-direction: column; gap: 10px; }

        /* ── Contact ── */
        .rp-contact-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px; }
        .rp-contact-item {
          display: flex; align-items: flex-start; gap: 12px;
          padding: 14px; border-radius: 14px; background: #f8fafc; border: 1px solid #f1f5f9;
          transition: border-color 0.2s;
        }
        .rp-contact-item:hover { border-color: #c7d2fe; }
        .rp-contact-item__icon {
          width: 36px; height: 36px; border-radius: 10px;
          background: #eef2ff; display: flex; align-items: center; justify-content: center;
          font-size: 16px; flex-shrink: 0;
        }
        .rp-contact-item__label { font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 400; margin-bottom: 3px; }
        .rp-contact-item__val { font-size: 14px; font-weight: 400; color: #1e293b; word-break: break-word; }
        .rp-contact-item__val a { color: #6366f1; text-decoration: none; }
        .rp-contact-item__val a:hover { text-decoration: underline; }

        /* ── Benefits ── */
        .rp-benefits-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; }
        .rp-benefit-chip {
          padding: 12px 16px; border-radius: 14px;
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          border: 1px solid #bbf7d0; color: #15803d;
          font-size: 13px; font-weight: 400; text-align: center;
          transition: transform 0.2s;
        }
        .rp-benefit-chip:hover { transform: translateY(-2px); }

        /* ── Salary card ── */
        .rp-salary-item {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 16px; border-radius: 14px; background: #f8fafc;
          border: 1px solid #f1f5f9; gap: 12px;
        }
        .rp-salary-item:hover { border-color: #bbf7d0; }
        .rp-salary-range { font-size: 15px; font-weight: 400; color: #15803d; }

        /* ── Mobile sidebar toggle ── */
        .rp-mobile-toggle {
          display: none; position: fixed; bottom: 24px; left: 24px; z-index: 100;
          width: 50px; height: 50px; border-radius: 50%;
          background: linear-gradient(135deg, #6366f1, #394420);
          border: none; color: #fff; font-size: 20px; cursor: pointer;
          box-shadow: 0 4px 20px rgba(99,102,241,0.4);
          align-items: center; justify-content: center;
        }

        /* ── Section header decoration ── */
        .rp-section-decoration {
          display: inline-flex; align-items: center; gap: 8px;
          font-size: 12px; font-weight: 400; text-transform: uppercase;
          letter-spacing: 0.12em; color: #6366f1;
          padding: 5px 12px; background: #eef2ff; border-radius: 8px;
        }

        /* ── Responsive ── */
        @media (max-width: 1024px) {
          .rp-metrics { grid-template-columns: repeat(2, 120px); }
        }
        @media (max-width: 900px) {
          .rp-shell { grid-template-columns: 1fr; }
          .rp-sidebar { position: static; max-height: none; display: none; }
          .rp-sidebar.is-open { display: flex; }
          .rp-mobile-toggle { display: flex; }
        }
        @media (max-width: 768px) {
          .rp-hero__main { grid-template-columns: 1fr; }
          .rp-metrics { grid-template-columns: repeat(3, 1fr); width: 100%; }
          .rp-brand { flex-direction: column; align-items: flex-start; }
          .rp-jobs-grid { grid-template-columns: 1fr; }
          .rp-details-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 480px) {
          .rp-metrics { grid-template-columns: repeat(2, 1fr); }
          .rp-details-grid { grid-template-columns: 1fr; }
          .rp-contact-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="rp-root">
        {/* ── HERO ── */}
        <section className="rp-hero">
          <div className="rp-hero__inner">
            <div className="rp-hero__topbar" data-aos="fade-down">
              <button className="rp-back-btn" onClick={() => navigate("/employer/recruiter-dashboard")}>
                ← Back to Dashboard
              </button>
              <div className="rp-hero__actions">
                <button className="rp-action-btn" onClick={() => navigate(`/employee/company/${profile?.id || ""}`)}>
                  🌐 Public Page
                </button>
                <button className="rp-action-btn rp-action-btn--primary" onClick={() => setIsEditing((v) => !v)}>
                  {isEditing ? "✕ Close Editor" : "✏️ Edit Profile"}
                </button>
              </div>
            </div>

            <div className="rp-hero__main">
              <div className="rp-brand" data-aos="fade-right">
                <div className="rp-brand__logo-wrap">
                  {companyLogo
                    ? <img src={companyLogo} alt={displayName} className="rp-brand__logo" />
                    : <div className="rp-brand__initials">{getInitials(displayName)}</div>}
                  {profile?.is_verified && <div className="rp-brand__verified">✓</div>}
                </div>
                <div className="rp-brand__info">
                  <div className="rp-brand__kicker">Recruiter Profile</div>
                  <h1 className="rp-brand__name">{displayName}</h1>
                  <p className="rp-brand__subtitle">
                    {profile?.industry ? `${profile.industry} · ` : ""}
                    {profile?.city ? `${profile.city} · ` : ""}
                    {activeJobs.length} active opening{activeJobs.length !== 1 ? "s" : ""}
                  </p>
                  {summaryTags.length > 0 && (
                    <div className="rp-tag-row">
                      {summaryTags.map((tag) => <span className="rp-tag" key={tag}>{tag}</span>)}
                    </div>
                  )}
                </div>
              </div>

              <div className="rp-metrics" data-aos="fade-left">
                {metrics.map((m, i) => (
                  <div
                    className="rp-metric"
                    key={m.label}
                    data-aos="zoom-in"
                    data-aos-delay={i * 60}
                  >
                    <div className="rp-metric__icon">{m.icon}</div>
                    <div className="rp-metric__label">{m.label}</div>
                    <div className="rp-metric__val">{String(m.value)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── SHELL ── */}
        <div className="rp-shell">
          {/* ── SIDEBAR ── */}
          <aside className={`rp-sidebar${mobileNavOpen ? " is-open" : ""}`} data-aos="fade-right">
            {/* Navigation */}
            <div className="rp-sidebar__card">
              <div className="rp-sidebar__card-title">Sections</div>
              {SECTIONS.map((sec) => (
                <button
                  key={sec.key}
                  className={`rp-nav-btn${activeSection === sec.key ? " is-active" : ""}`}
                  onClick={() => { setActiveSection(sec.key); setMobileNavOpen(false); }}
                >
                  <span className="rp-nav-btn__icon">{sec.icon}</span>
                  {sec.label}
                  {sec.key === "jobs" && <span className="rp-nav-btn__count">{jobs.length}</span>}
                </button>
              ))}
            </div>

            {/* Quick info */}
            <div className="rp-sidebar__card">
              <div className="rp-sidebar__card-title">Quick Info</div>
              <div className="rp-sidebar__info">
                {[
                  { label: "Email", value: contactEmail || "Not set" },
                  { label: "Phone", value: contactPhone || "Not set" },
                  { label: "Website", value: website || "Not set" },
                  { label: "LinkedIn", value: linkedin ? "Connected" : "Not set" },
                ].map((row) => (
                  <div key={row.label} className="rp-sidebar__info-row">
                    <span>{row.label}</span>
                    <strong>{row.value}</strong>
                  </div>
                ))}
              </div>
            </div>

            {/* Profile completion */}
            <div className="rp-sidebar__card">
              <div className="rp-sidebar__card-title">Profile Strength</div>
              <div className="rp-progress-wrap">
                <div className="rp-progress__label">
                  <span>Completion</span>
                  <span style={{ color: profileCompletion < 50 ? "#263012" : profileCompletion < 80 ? "#6366f1" : "#15803d" }}>
                    {profileCompletion}%
                  </span>
                </div>
                <div className="rp-progress__track">
                  <div
                    className="rp-progress__fill"
                    style={{
                      width: `${profileCompletion}%`,
                      "--pct": `${profileCompletion}%`,
                      background: profileCompletion < 50 ? "linear-gradient(90deg,#f59e0b,#263012)"
                        : profileCompletion < 80 ? "linear-gradient(90deg,#6366f1,#394420)"
                        : "linear-gradient(90deg,#263012,#394420)",
                    }}
                  />
                </div>
              </div>
            </div>
          </aside>

          {/* ── CONTENT ── */}
          <main className="rp-content">
            {loading && (
              <div className="rp-state">
                <div className="rp-spinner" style={{ width: 28, height: 28, margin: "0 auto 12px", borderWidth: 3 }} />
                Loading company profile…
              </div>
            )}
            {!loading && error && <div className="rp-state rp-state--error">⚠️ {error}</div>}

            {!loading && !error && (
              <>
                {isEditing && formError && <div className="rp-state rp-state--error">{formError}</div>}
                {/* ── ABOUT ── */}
                {activeSection === "about" && (
                  <div className="rp-card" data-aos="fade-up">
                    <div className="rp-card__head">
                      <div className="rp-card__head-left">
                        <div className="rp-card__icon">🏢</div>
                        <h2>About the Company</h2>
                      </div>
                      <div className="rp-card__actions">
                        <span className={`rp-pill${profile?.status === "active" ? "" : " rp-pill--warn"}`}>
                          {profile?.status || "pending"}
                        </span>
                        <button className="rp-edit-btn" onClick={() => setIsEditing((v) => !v)}>
                          {isEditing ? "✕ Close" : "✏️ Edit"}
                        </button>
                      </div>
                    </div>

                    {isEditing ? (
                      <div className="rp-edit-form">
                        <div className="rp-form-note">
                          Fill in official company data only. Required fields are validated before save.
                        </div>
                        <div className="rp-grid">
                          <label className="rp-field"><span className="rp-field__label">First Name</span><input type="text" value={draft.first_name} onChange={(e) => setDraft((p) => ({ ...p, first_name: e.target.value }))} /></label>
                          <label className="rp-field"><span className="rp-field__label">Last Name</span><input type="text" value={draft.last_name} onChange={(e) => setDraft((p) => ({ ...p, last_name: e.target.value }))} /></label>
                          <label className="rp-field"><span className="rp-field__label">Recruiter Role</span><input type="text" value={draft.role} onChange={(e) => setDraft((p) => ({ ...p, role: e.target.value }))} placeholder="HR Manager" /></label>
                          <label className="rp-field"><span className="rp-field__label">Department</span><input type="text" value={draft.dept} onChange={(e) => setDraft((p) => ({ ...p, dept: e.target.value }))} placeholder="Talent Acquisition" /></label>
                        </div>
                        <div className="rp-grid">
                          <label className="rp-field"><span className="rp-field__label">Company Name</span><input type="text" value={draft.company} onChange={(e) => setDraft((p) => ({ ...p, company: e.target.value }))} /></label>
                          <label className="rp-field"><span className="rp-field__label">Industry</span><input type="text" value={draft.industry} onChange={(e) => setDraft((p) => ({ ...p, industry: e.target.value }))} /></label>
                          <label className="rp-field"><span className="rp-field__label">Company Type</span><input type="text" value={draft.company_type} onChange={(e) => setDraft((p) => ({ ...p, company_type: e.target.value }))} placeholder="Startup, MNC, Agency…" /></label>
                          <label className="rp-field"><span className="rp-field__label">Company Size</span><input type="text" value={draft.size} onChange={(e) => setDraft((p) => ({ ...p, size: e.target.value }))} placeholder="51–200" /></label>
                        </div>
                        <div className="rp-grid">
                          <label className="rp-field"><span className="rp-field__label">Website</span><input type="url" value={draft.website} onChange={(e) => setDraft((p) => ({ ...p, website: e.target.value }))} placeholder="https://company.com" /></label>
                          <label className="rp-field"><span className="rp-field__label">LinkedIn</span><input type="url" value={draft.linkedin} onChange={(e) => setDraft((p) => ({ ...p, linkedin: e.target.value }))} placeholder="https://www.linkedin.com/company/your-company" /></label>
                          <label className="rp-field"><span className="rp-field__label">Logo URL</span><input type="url" value={draft.company_logo} onChange={(e) => setDraft((p) => ({ ...p, company_logo: e.target.value }))} /></label>
                          <label className="rp-field"><span className="rp-field__label">Source</span><input type="text" value={draft.source} onChange={(e) => setDraft((p) => ({ ...p, source: e.target.value }))} /></label>
                        </div>
                        <div className="rp-grid">
                          <label className="rp-field"><span className="rp-field__label">Work Email (read-only)</span><input type="email" value={profile?.email || ""} disabled /></label>
                          <label className="rp-field"><span className="rp-field__label">Company Email</span><input type="email" value={draft.company_email} onChange={(e) => setDraft((p) => ({ ...p, company_email: e.target.value }))} /></label>
                          <label className="rp-field"><span className="rp-field__label">Primary Phone (read-only)</span><input type="text" value={contactPhone} disabled /></label>
                          <label className="rp-field"><span className="rp-field__label">Alternate Phone</span><input type="tel" value={draft.alt_phone} onChange={(e) => setDraft((p) => ({ ...p, alt_phone: e.target.value }))} /></label>
                        </div>
                        <div className="rp-grid">
                          <label className="rp-field"><span className="rp-field__label">City</span><input type="text" value={draft.city} onChange={(e) => setDraft((p) => ({ ...p, city: e.target.value }))} /></label>
                          <label className="rp-field"><span className="rp-field__label">State</span><input type="text" value={draft.state} onChange={(e) => setDraft((p) => ({ ...p, state: e.target.value }))} /></label>
                          <label className="rp-field"><span className="rp-field__label">Country</span><input type="text" value={draft.country} onChange={(e) => setDraft((p) => ({ ...p, country: e.target.value }))} /></label>
                          <label className="rp-field"><span className="rp-field__label">PIN / ZIP</span><input type="text" maxLength={10} value={draft.pin} onChange={(e) => setDraft((p) => ({ ...p, pin: e.target.value }))} /></label>
                        </div>
                        <label className="rp-field rp-field--full"><span className="rp-field__label">Address</span><textarea rows={3} value={draft.address} onChange={(e) => setDraft((p) => ({ ...p, address: e.target.value }))} /></label>
                        <div className="rp-grid">
                          <label className="rp-field"><span className="rp-field__label">Year Established</span><input type="number" min="1800" max={new Date().getFullYear()} value={draft.year_established} onChange={(e) => setDraft((p) => ({ ...p, year_established: e.target.value }))} /></label>
                          <label className="rp-field"><span className="rp-field__label">Branches</span><input type="number" min="0" value={draft.branches} onChange={(e) => setDraft((p) => ({ ...p, branches: e.target.value }))} /></label>
                          <label className="rp-field"><span className="rp-field__label">Open Positions</span><input type="number" min="0" value={draft.open_positions} onChange={(e) => setDraft((p) => ({ ...p, open_positions: e.target.value }))} /></label>
                          <label className="rp-field"><span className="rp-field__label">Hiring Urgency</span>
                            <select value={draft.urgency} onChange={(e) => setDraft((p) => ({ ...p, urgency: e.target.value }))}>
                              <option value="">Select urgency</option>
                              <option>Immediate</option><option>High</option><option>Medium</option><option>Low</option>
                            </select>
                          </label>
                        </div>
                        <div className="rp-grid">
                          <label className="rp-field"><span className="rp-field__label">Employment Types</span><input type="text" value={draft.employment_types} onChange={(e) => setDraft((p) => ({ ...p, employment_types: e.target.value }))} placeholder="Full-time, Part-time, Remote" /></label>
                          <label className="rp-field"><span className="rp-field__label">Experience Levels</span><input type="text" value={draft.experience_levels} onChange={(e) => setDraft((p) => ({ ...p, experience_levels: e.target.value }))} placeholder="Fresher, Mid-level, Senior" /></label>
                          <label className="rp-field"><span className="rp-field__label">GST Number</span><input type="text" maxLength={15} value={draft.gst} onChange={(e) => setDraft((p) => ({ ...p, gst: e.target.value.toUpperCase() }))} /></label>
                        </div>
                        <label className="rp-field rp-field--full"><span className="rp-field__label">About / Hiring Bio</span><textarea rows={5} value={draft.hiring_bio} onChange={(e) => setDraft((p) => ({ ...p, hiring_bio: e.target.value }))} placeholder="Tell candidates about your company culture, mission, and hiring style." /></label>
                        <div className="rp-edit-actions">
                          <button className="rp-edit-btn" onClick={() => setIsEditing(false)} disabled={saving}>Cancel</button>
                          <button className="rp-edit-btn rp-edit-btn--primary" onClick={saveProfile} disabled={saving}>{saving ? "Saving…" : "💾 Save Changes"}</button>
                        </div>
                      </div>
                    ) : (
                      <div className="rp-card__body">
                        <p className="rp-about__text">{aboutText}</p>
                        {summaryTags.length > 0 && (
                          <div className="rp-tag-row" style={{ marginBottom: 22 }}>
                            {summaryTags.map((t) => (
                              <span key={t} style={{ padding: "5px 14px", borderRadius: 8, background: "#eef2ff", color: "#4338ca", fontSize: 13, fontWeight: 600, border: "1px solid #c7d2fe" }}>{t}</span>
                            ))}
                          </div>
                        )}
                        <div className="rp-details-grid">
                          {[
                            { label: "Industry", value: profile?.industry },
                            { label: "Company Size", value: profile?.size },
                            { label: "Location", value: profile?.city },
                            { label: "Verified", value: profile?.is_verified ? "✅ Yes" : "Not yet" },
                            { label: "Company Type", value: profile?.company_type },
                            { label: "Year Established", value: profile?.year_established },
                            { label: "Branches", value: profile?.branches || "0" },
                            { label: "Open Positions", value: profile?.open_positions || "0" },
                          ].map((item, i) => (
                            <div className="rp-detail-box" key={item.label} data-aos="zoom-in" data-aos-delay={i * 60}>
                              <div className="rp-detail-box__label">{item.label}</div>
                              <div className="rp-detail-box__val">{item.value || "Not set"}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── CONTACT ── */}
                {activeSection === "contact" && (
                  <div className="rp-card" data-aos="fade-up">
                    <div className="rp-card__head">
                      <div className="rp-card__head-left">
                        <div className="rp-card__icon">📞</div>
                        <h2>Contact Information</h2>
                      </div>
                      <div className="rp-card__actions">
                        <span className="rp-pill rp-pill--blue">Recruiter & Company</span>
                        <button className="rp-edit-btn" onClick={() => setIsEditing((v) => !v)}>
                          {isEditing ? "✕ Close" : "✏️ Edit"}
                        </button>
                      </div>
                    </div>
                    {isEditing ? (
                      <div className="rp-edit-form">
                        <div className="rp-grid">
                          {["recruiterName","role","dept","phone","altPhone","email","website","linkedin"].map((key, i) => (
                            <label className="rp-field" key={key}>
                              <span className="rp-field__label">{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</span>
                              <input type="text" value={activeDraft[key] || ""} onChange={(e) => setSectionField("contact", key, e.target.value)} />
                            </label>
                          ))}
                        </div>
                        <div className="rp-edit-actions">
                          <button className="rp-edit-btn" onClick={() => setIsEditing(false)} disabled={saving}>Cancel</button>
                          <button className="rp-edit-btn rp-edit-btn--primary" onClick={saveProfile} disabled={saving}>{saving ? "Saving…" : "💾 Save"}</button>
                        </div>
                      </div>
                    ) : (
                      <div className="rp-card__body">
                        <div className="rp-contact-grid">
                          {[
                            { icon: "👤", label: "Recruiter Name", value: activeDraft.recruiterName },
                            { icon: "💼", label: "Role", value: activeDraft.role },
                            { icon: "🏢", label: "Department", value: activeDraft.dept },
                            { icon: "📱", label: "Primary Phone", value: activeDraft.phone },
                            { icon: "📲", label: "Alternate Phone", value: activeDraft.altPhone },
                            { icon: "✉️", label: "Company Email", value: activeDraft.email },
                            { icon: "🌐", label: "Website", value: activeDraft.website, isLink: true },
                            { icon: "🔗", label: "LinkedIn", value: activeDraft.linkedin, isLink: true },
                          ].map((item, i) => (
                            <div className="rp-contact-item" key={item.label} data-aos="fade-up" data-aos-delay={i * 60}>
                              <div className="rp-contact-item__icon">{item.icon}</div>
                              <div>
                                <div className="rp-contact-item__label">{item.label}</div>
                                <div className="rp-contact-item__val">
                                  {item.isLink && item.value
                                    ? <a href={item.value} target="_blank" rel="noopener noreferrer">{item.value}</a>
                                    : item.value || <span style={{ color: "#c4c9d4", fontStyle: "italic" }}>Not set</span>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── HIRING ── */}
                {activeSection === "hiring" && (
                  <div className="rp-card" data-aos="fade-up">
                    <div className="rp-card__head">
                      <div className="rp-card__head-left">
                        <div className="rp-card__icon">🎯</div>
                        <h2>Hiring Details</h2>
                      </div>
                      <div className="rp-card__actions">
                        <span className="rp-pill rp-pill--purple">Public Hiring Info</span>
                        <button className="rp-edit-btn" onClick={() => setIsEditing((v) => !v)}>
                          {isEditing ? "✕ Close" : "✏️ Edit"}
                        </button>
                      </div>
                    </div>
                    {isEditing ? (
                      <div className="rp-edit-form">
                        <label className="rp-field"><span className="rp-field__label">Hiring Summary</span><textarea rows={4} value={activeDraft.summary || ""} onChange={(e) => setSectionField("hiring", "summary", e.target.value)} /></label>
                        <div className="rp-grid">
                          {[["companyType","Company Type"],["size","Company Size"],["yearEstablished","Year Established"],["branches","Branches"],["openPositions","Open Positions"],["urgency","Urgency"],["location","Location"],["country","Country"]].map(([key, label]) => (
                            <label className="rp-field" key={key}><span className="rp-field__label">{label}</span><input type="text" value={activeDraft[key] || ""} onChange={(e) => setSectionField("hiring", key, e.target.value)} /></label>
                          ))}
                        </div>
                        <label className="rp-field rp-field--full"><span className="rp-field__label">Address</span><textarea rows={3} value={activeDraft.address || ""} onChange={(e) => setSectionField("hiring", "address", e.target.value)} /></label>
                        <div className="rp-edit-actions">
                          <button className="rp-edit-btn" onClick={() => setIsEditing(false)} disabled={saving}>Cancel</button>
                          <button className="rp-edit-btn rp-edit-btn--primary" onClick={saveProfile} disabled={saving}>{saving ? "Saving…" : "💾 Save"}</button>
                        </div>
                      </div>
                    ) : (
                      <div className="rp-card__body">
                        {activeDraft.summary && <p className="rp-about__text">{activeDraft.summary}</p>}
                        <div className="rp-details-grid">
                          {[
                            { label: "Location", value: activeDraft.location },
                            { label: "Country", value: activeDraft.country },
                            { label: "Address", value: activeDraft.address },
                            { label: "Urgency", value: activeDraft.urgency },
                            { label: "Employment Types", value: activeDraft.employment_types || toTextList(profile?.employment_types) },
                            { label: "Experience Levels", value: activeDraft.experience_levels || toTextList(profile?.experience_levels) },
                          ].map((item, i) => (
                            <div className="rp-detail-box" key={item.label} data-aos="fade-up" data-aos-delay={i * 60}>
                              <div className="rp-detail-box__label">{item.label}</div>
                              <div className="rp-detail-box__val">{item.value || <span style={{ color: "#c4c9d4", fontStyle: "italic" }}>Not set</span>}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── JOBS ── */}
                {activeSection === "jobs" && (
                  <div className="rp-card" data-aos="fade-up">
                    <div className="rp-card__head">
                      <div className="rp-card__head-left">
                        <div className="rp-card__icon">💼</div>
                        <h2>Open Positions</h2>
                      </div>
                      <div className="rp-card__actions">
                        <span className="rp-pill">{activeJobs.length} live</span>
                        <span className="rp-pill rp-pill--blue">{jobs.length} total</span>
                      </div>
                    </div>
                    {jobs.length === 0 ? (
                      <div className="rp-empty">
                        <div className="rp-empty__icon">💼</div>
                        <div className="rp-empty__title">No jobs posted yet</div>
                        <div style={{ fontSize: 13, color: "#94a3b8" }}>Jobs will appear here once posted from your dashboard.</div>
                      </div>
                    ) : (
                      <div className="rp-jobs-grid">
                        {jobs.map((job, i) => (
                          <RecruiterJobCard
                            key={job.id}
                            job={job}
                            index={i}
                            appliedJobs={appliedJobs}
                            applyingJobId={applyingJobId}
                            onApplyClick={handleApplyClick}
                            onViewDesc={setSelectedJob}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── SALARIES ── */}
                {activeSection === "salaries" && (
                  <div className="rp-card" data-aos="fade-up">
                    <div className="rp-card__head">
                      <div className="rp-card__head-left">
                        <div className="rp-card__icon">💰</div>
                        <h2>Salary Insights</h2>
                      </div>
                      <span className="rp-pill">From posted jobs</span>
                    </div>
                    {jobs.length === 0 ? (
                      <div className="rp-empty">
                        <div className="rp-empty__icon">💰</div>
                        <div className="rp-empty__title">No salary data yet</div>
                        <div style={{ fontSize: 13, color: "#94a3b8" }}>Salary insights will appear once jobs with salary info are posted.</div>
                      </div>
                    ) : (
                      <div className="rp-card__body">
                        <div className="rp-list">
                          {jobs.map((job, i) => (
                            <div className="rp-salary-item" key={job.id} data-aos="fade-up" data-aos-delay={i * 60}>
                              <div>
                                <strong style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{job.title}</strong>
                                <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>{job.dept} · {job.type}</p>
                              </div>
                              <div className="rp-salary-range">{job.salary}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── INTERVIEWS ── */}
                {activeSection === "interviews" && (
                  <div className="rp-card" data-aos="fade-up">
                    <div className="rp-card__head">
                      <div className="rp-card__head-left">
                        <div className="rp-card__icon">🤝</div>
                        <h2>Interview Process</h2>
                      </div>
                      <div className="rp-card__actions">
                        <span className="rp-pill rp-pill--purple">Recruiter Workflow</span>
                        <button className="rp-edit-btn" onClick={() => setIsEditing((v) => !v)}>
                          {isEditing ? "✕ Close" : "✏️ Edit"}
                        </button>
                      </div>
                    </div>
                    {isEditing ? (
                      <div className="rp-edit-form">
                        <label className="rp-field"><span className="rp-field__label">Interview Headline</span><input type="text" value={activeDraft.headline || ""} onChange={(e) => setSectionField("interviews", "headline", e.target.value)} /></label>
                        <label className="rp-field rp-field--full"><span className="rp-field__label">Interview Notes</span><textarea rows={4} value={activeDraft.notes || ""} onChange={(e) => setSectionField("interviews", "notes", e.target.value)} /></label>
                        <div className="rp-edit-actions">
                          <button className="rp-edit-btn" onClick={() => setIsEditing(false)} disabled={saving}>Cancel</button>
                          <button className="rp-edit-btn rp-edit-btn--primary" onClick={saveProfile} disabled={saving}>{saving ? "Saving…" : "💾 Save"}</button>
                        </div>
                      </div>
                    ) : (
                      <div className="rp-card__body">
                        {activeDraft.headline && <h3 style={{ fontSize: 17, fontWeight: 600, marginBottom: 12 }}>{activeDraft.headline}</h3>}
                        {activeDraft.notes ? (
                          <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{activeDraft.notes}</p>
                        ) : (
                          <div className="rp-empty">
                            <div className="rp-empty__icon">🤝</div>
                            <div className="rp-empty__title">No interview info yet</div>
                            <div style={{ fontSize: 13, color: "#94a3b8" }}>Add details about your interview process, rounds, and timeline.</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ── BENEFITS ── */}
                {activeSection === "benefits" && (
                  <div className="rp-card" data-aos="fade-up">
                    <div className="rp-card__head">
                      <div className="rp-card__head-left">
                        <div className="rp-card__icon">🎁</div>
                        <h2>Benefits & Perks</h2>
                      </div>
                      <div className="rp-card__actions">
                        <span className="rp-pill">Company Perks</span>
                        <button className="rp-edit-btn" onClick={() => setIsEditing((v) => !v)}>
                          {isEditing ? "✕ Close" : "✏️ Edit"}
                        </button>
                      </div>
                    </div>
                    {isEditing ? (
                      <div className="rp-edit-form">
                        <label className="rp-field"><span className="rp-field__label">Benefits Headline</span><input type="text" value={activeDraft.headline || ""} onChange={(e) => setSectionField("benefits", "headline", e.target.value)} /></label>
                        <label className="rp-field rp-field--full"><span className="rp-field__label">Benefits List (comma-separated)</span><textarea rows={4} value={activeDraft.items || ""} onChange={(e) => setSectionField("benefits", "items", e.target.value)} placeholder="Health Insurance, Remote Work, Annual Bonus, Flexible Hours…" /></label>
                        <div className="rp-edit-actions">
                          <button className="rp-edit-btn" onClick={() => setIsEditing(false)} disabled={saving}>Cancel</button>
                          <button className="rp-edit-btn rp-edit-btn--primary" onClick={saveProfile} disabled={saving}>{saving ? "Saving…" : "💾 Save"}</button>
                        </div>
                      </div>
                    ) : (
                      <div className="rp-card__body">
                        {activeDraft.headline && <h3 style={{ fontSize: 17, fontWeight: 600, marginBottom: 16 }}>{activeDraft.headline}</h3>}
                        {activeDraft.items ? (
                          <div className="rp-benefits-grid">
                            {toList(activeDraft.items).map((benefit, i) => (
                              <div className="rp-benefit-chip" key={i} data-aos="zoom-in" data-aos-delay={i * 60}>
                                {benefit}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="rp-empty">
                            <div className="rp-empty__icon">🎁</div>
                            <div className="rp-empty__title">No benefits listed yet</div>
                            <div style={{ fontSize: 13, color: "#94a3b8" }}>Add health insurance, remote work, bonuses, and other perks.</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ── PHOTOS ── */}
                {activeSection === "photos" && (
                  <div className="rp-card" data-aos="fade-up">
                    <div className="rp-card__head">
                      <div className="rp-card__head-left">
                        <div className="rp-card__icon">📸</div>
                        <h2>Company Photos</h2>
                      </div>
                      <div className="rp-card__actions">
                        <span className="rp-pill rp-pill--blue">Branding</span>
                        <button className="rp-edit-btn" onClick={() => setIsEditing((v) => !v)}>
                          {isEditing ? "✕ Close" : "✏️ Edit"}
                        </button>
                      </div>
                    </div>
                    <div className="rp-card__body">
                      {companyLogo ? (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 14 }}>
                          <img src={companyLogo} alt={displayName} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 16, border: "1px solid #e2e8f0" }} data-aos="zoom-in" />
                        </div>
                      ) : (
                        <div className="rp-empty">
                          <div className="rp-empty__icon">📸</div>
                          <div className="rp-empty__title">No photos uploaded yet</div>
                          <div style={{ fontSize: 13, color: "#94a3b8" }}>Upload a company logo or office photos to showcase your brand.</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── POSTS ── */}
                {activeSection === "posts" && (
                  <div className="rp-card" data-aos="fade-up">
                    <div className="rp-card__head">
                      <div className="rp-card__head-left">
                        <div className="rp-card__icon">📢</div>
                        <h2>Company Posts</h2>
                      </div>
                      <span className="rp-pill">Updates</span>
                    </div>
                    {jobs.length === 0 ? (
                      <div className="rp-empty">
                        <div className="rp-empty__icon">📢</div>
                        <div className="rp-empty__title">No posts yet</div>
                        <div style={{ fontSize: 13, color: "#94a3b8" }}>Company posts will appear here once the post feed is connected.</div>
                      </div>
                    ) : (
                      <div className="rp-card__body">
                        <div className="rp-list">
                          {jobs.slice(0, 5).map((job, i) => (
                            <div className="rp-list-item" key={job.id} data-aos="fade-up" data-aos-delay={i * 60}>
                              <div style={{ flex: 1 }}>
                                <strong>{job.title}</strong>
                                <p>{job.description ? job.description.substring(0, 100) + "…" : "A new opening from this company."}</p>
                              </div>
                              <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, white_space: "nowrap", flex_shrink: 0 }}>Job Post</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </main>
        </div>

        {/* ── Mobile nav toggle ── */}
        <button className="rp-mobile-toggle" onClick={() => setMobileNavOpen((v) => !v)} title="Toggle navigation">
          {mobileNavOpen ? "✕" : "☰"}
        </button>

        {/* ── Modals & Toasts ── */}
        {selectedJob && (
          <JobDetailModal
            job={selectedJob}
            onClose={() => setSelectedJob(null)}
            appliedJobs={appliedJobs}
            applyingJobId={applyingJobId}
            onApplyClick={handleApplyClick}
          />
        )}

        <ProfileIncompletePopup
          open={profilePopupOpen}
          onClose={() => { setProfilePopupOpen(false); setPendingJob(null); }}
          targetJob={pendingJob}
        />

        <SuccessToast
          open={successToast.open}
          job={successToast.job}
          onClose={() => setSuccessToast({ open: false, job: null })}
        />

        <ErrorToast
          open={errorToast.open}
          message={errorToast.message}
          onClose={() => setErrorToast({ open: false, message: "" })}
        />
      </div>
    </>
  );
}
